import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SectionEyebrow from "@components/common/SectionEyebrow";
import ThemedIcon from "@components/common/ThemedIcon";
import useInView from "@hooks/useInView";
import { services } from "@data/services";

const DURATION = 4500;
const FADE_MS = 180;

/* ── Mobile / tablet accordion item ── */
function AccordionItem({ svc, isOpen, onToggle, isHinted }) {
  return (
    <div
      className={`rounded-2xl overflow-hidden border transition-all duration-300 ${
        isOpen
          ? "border-konecta-orange/40 bg-white/[0.03]"
          : isHinted
          ? "border-konecta-orange/20 bg-white/[0.015] animate-pulse-subtle"
          : "border-white/[0.08] bg-white/[0.015]"
      }`}
    >
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
      >
        <span
          className={`font-heading font-extrabold text-xl leading-none shrink-0 transition-colors ${
            isOpen ? "text-konecta-orange" : "text-white/20"
          }`}
        >
          {svc.number}
        </span>

        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
            isOpen
              ? "bg-konecta-orange/15 text-konecta-orange"
              : "bg-white/[0.05] text-white/50"
          }`}
        >
          <ThemedIcon name={svc.icon} size={18} />
        </div>

        <span
          className={`font-heading font-bold text-sm flex-1 leading-snug transition-colors ${
            isOpen ? "text-konecta-white" : "text-white/70"
          }`}
        >
          {svc.title}
        </span>

        {/* Tap hint on closed items */}
        {!isOpen && (
          <span className="text-[10px] text-white/30 uppercase tracking-wider mr-1 hidden xs:block">
            Tap
          </span>
        )}

        <svg
          className={`w-4 h-4 shrink-0 transition-all duration-300 ${
            isOpen ? "text-konecta-orange rotate-180" : "text-white/30"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expandable body */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="relative h-48 mx-4 rounded-xl overflow-hidden">
          <img
            src={svc.image}
            alt={svc.title}
            loading="lazy"
            className={`absolute inset-0 w-full h-full ${
              svc.imageContain ? "object-contain p-4" : "object-cover"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-konecta-black/40 via-transparent to-transparent" />
        </div>

        <div className="mx-4 mt-3 mb-4 flex flex-wrap gap-1.5">
          {svc.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-0.5 rounded-full text-[10px] font-heading font-bold text-konecta-orange uppercase tracking-wider"
              style={{
                background: "rgba(244,129,32,0.10)",
                border: "1px solid rgba(244,129,32,0.25)",
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="px-5 pb-6">
          <p className="text-sm text-white/80 leading-relaxed mb-5">
            {svc.description}
          </p>

          {svc.capabilities && (
            <ul className="space-y-2.5 mb-6">
              {svc.capabilities.map((cap) => (
                <li key={cap} className="flex items-start gap-3 text-sm text-white/75">
                  <span className="w-1.5 h-1.5 rounded-full bg-konecta-orange mt-1.5 shrink-0" />
                  {cap}
                </li>
              ))}
            </ul>
          )}

          {svc.link ? (
            <Link to={svc.link} className="btn-primary w-full text-center block">
              Explore Service
            </Link>
          ) : (
            <Link to="/contact" className="btn-secondary w-full text-center block">
              Get in Touch
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function ServicesGrid() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [shownIdx, setShownIdx] = useState(0);
  const [panelVisible, setPanelVisible] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const [barWidth, setBarWidth] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(0);
  const [sectionRef, inView] = useInView({ threshold: 0.1 });
  const fadeTimer = useRef(null);

  // Cross-fade: fade out → swap content → fade in
  useEffect(() => {
    if (activeIdx === shownIdx) return;
    setPanelVisible(false);
    clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(() => {
      setShownIdx(activeIdx);
      setPanelVisible(true);
    }, FADE_MS);
    return () => clearTimeout(fadeTimer.current);
  }, [activeIdx]);

  const active = services[shownIdx];

  useEffect(() => {
    if (!autoPlay || !inView) return;

    setBarWidth(0);
    const startBar = setTimeout(() => setBarWidth(100), 40);
    const advance = setTimeout(() => {
      setActiveIdx((prev) => (prev + 1) % services.length);
    }, DURATION);

    return () => {
      clearTimeout(startBar);
      clearTimeout(advance);
    };
  }, [activeIdx, autoPlay, inView]);

  const handleSelect = (i) => {
    setAutoPlay(false);
    setBarWidth(0);
    setActiveIdx(i);
  };

  return (
    <section
      ref={sectionRef}
      className="relative bg-konecta-black content-px py-section overflow-hidden border-t border-white/[0.1]"
      id="services"
    >
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-konecta-orange/[0.04] blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-konecta-slate/[0.03] blur-[100px] rounded-full pointer-events-none" />

      {/* Header */}
      <div
        className={`relative z-10 mb-14 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >
        <SectionEyebrow text="What We Do" />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-4">
          <h2 className="section-title max-w-xl">
            Our <span className="text-gradient-orange">Services</span>
          </h2>
          <p className="text-base text-white/55 max-w-md leading-relaxed lg:text-right">
            From wireless connectivity to AI-powered platforms — we build,
            deploy, and manage technology that empowers African businesses to
            connect, grow, and compete.
          </p>
        </div>
      </div>

      <div
        className={`relative z-10 transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >

        {/* ════════════════════════════════════════
            MOBILE / TABLET  — accordion (< lg)
            ════════════════════════════════════════ */}
        <div className="lg:hidden">
          {/* Mobile hint */}
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="text-[0.7rem] text-white/35 uppercase tracking-wider">
              Tap each service to explore
            </p>
            <div className="flex items-center gap-1.5">
              {services.map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-300 ${
                    openAccordion === i
                      ? "w-4 h-1.5 bg-konecta-orange"
                      : "w-1.5 h-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {services.map((svc, i) => (
              <AccordionItem
                key={svc.id}
                svc={svc}
                isOpen={openAccordion === i}
                isHinted={openAccordion !== i && i === 1}
                onToggle={() => setOpenAccordion(openAccordion === i ? -1 : i)}
              />
            ))}
          </div>
        </div>

        {/* ════════════════════════════════════════
            DESKTOP  — tab + panel (lg+)
            ════════════════════════════════════════ */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-[280px_1fr] rounded-2xl overflow-hidden border border-white/[0.08]">

            {/* Left: vertical tab nav */}
            <div className="bg-white/[0.02] border-r border-white/[0.08] flex flex-col">
              {services.map((svc, i) => (
                <button
                  key={svc.id}
                  onClick={() => handleSelect(i)}
                  className={`group flex items-center gap-4 px-6 py-5 text-left transition-all duration-300 relative overflow-hidden ${
                    i === activeIdx
                      ? "bg-konecta-orange/10 border-l-2 border-konecta-orange"
                      : "border-l-2 border-transparent hover:bg-white/[0.03]"
                  }`}
                >
                  <span
                    className={`font-heading font-extrabold text-2xl leading-none transition-colors ${
                      i === activeIdx ? "text-konecta-orange" : "text-white/15 group-hover:text-white/30"
                    }`}
                  >
                    {svc.number}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`font-heading font-bold text-sm leading-snug transition-colors ${
                        i === activeIdx ? "text-konecta-white" : "text-white/70 group-hover:text-white/90"
                      }`}
                    >
                      {svc.title}
                    </div>
                    {i === activeIdx && (
                      <div className="w-6 h-0.5 bg-konecta-orange rounded-full mt-1.5" />
                    )}
                  </div>

                  {/* Progress bar on active tab */}
                  {i === activeIdx && autoPlay && (
                    <div
                      className="absolute bottom-0 left-0 h-[2px] bg-konecta-orange/60 rounded-full"
                      style={{
                        width: `${barWidth}%`,
                        transition:
                          barWidth === 100
                            ? `width ${DURATION - 40}ms linear`
                            : "none",
                      }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Right: detail + image */}
            <div className="grid grid-cols-2">
              <div
                className="p-10 flex flex-col bg-konecta-black/40"
                style={{
                  opacity: panelVisible ? 1 : 0,
                  transform: panelVisible ? "translateY(0)" : "translateY(8px)",
                  transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
                }}
              >
                <div className="font-heading font-extrabold text-[5rem] leading-none text-white/[0.04] select-none -mt-2 -ml-1 mb-1">
                  {active.number}
                </div>
                <ThemedIcon name={active.icon} size={34} className="text-konecta-orange mb-4 -mt-6" />
                <h3
                  className="font-heading font-extrabold text-konecta-white leading-tight mb-3"
                  style={{ fontSize: "clamp(1.35rem, 2vw, 1.8rem)" }}
                >
                  {active.title}
                </h3>
                <p className="text-base text-white/90 leading-relaxed mb-6">
                  {active.description}
                </p>
                {active.capabilities && (
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {active.capabilities.map((cap) => (
                      <li key={cap} className="flex items-start gap-3 text-base text-white/85">
                        <span className="w-1.5 h-1.5 rounded-full bg-konecta-orange mt-1.5 shrink-0" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto">
                  {active.link ? (
                    <Link to={active.link} className="btn-primary">
                      Explore Service
                    </Link>
                  ) : (
                    <Link to="/contact" className="btn-secondary">
                      Get in Touch
                    </Link>
                  )}
                </div>
              </div>

              <div className="relative overflow-hidden">
                <img
                  src={active.image}
                  alt={active.title}
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full ${active.imageContain ? "object-contain p-6" : "object-cover"}`}
                  style={{
                    opacity: panelVisible ? 1 : 0,
                    transform: panelVisible ? "scale(1)" : "scale(1.03)",
                    transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS * 2}ms ease`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-konecta-black/80 via-konecta-black/20 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-konecta-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex flex-wrap gap-2">
                    {active.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-[11px] font-heading font-bold text-konecta-orange uppercase tracking-wider backdrop-blur-sm"
                        style={{
                          background: "rgba(244,129,32,0.12)",
                          border: "1px solid rgba(244,129,32,0.25)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Desktop hint + dots */}
          <div className="flex items-center justify-between mt-3 px-1">
            <p className="text-[0.7rem] text-white/35 uppercase tracking-wider">
              {autoPlay ? "Auto-advancing — select a service to explore" : "Select a service to explore"}
            </p>
            <div className="flex items-center gap-2">
              {services.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleSelect(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === activeIdx
                      ? "w-5 h-1.5 bg-konecta-orange"
                      : "w-1.5 h-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
